import type { PreisToolFacet } from '@/api/preis-tool/preis-tool.types';
import {
  DsMultiSelect,
  DsSearchInput,
  type DsMultiSelectOption,
} from '@/modules/internal-v2/ds';

interface FilterToolbarProps {
  query: string;
  setQuery: (v: string) => void;
  selectedMfgs: number[];
  setSelectedMfgs: (v: number[]) => void;
  selectedGroups: number[];
  setSelectedGroups: (v: number[]) => void;
  manufacturers: PreisToolFacet[];
  itemGroups: PreisToolFacet[];
  onlyDemand: boolean;
  setOnlyDemand: (v: boolean) => void;
  onlyAnomalies: boolean;
  setOnlyAnomalies: (v: boolean) => void;
  onlyEdited: boolean;
  setOnlyEdited: (v: boolean) => void;
  onlyNotInSap: boolean;
  setOnlyNotInSap: (v: boolean) => void;
  priceFilter: 'priced' | 'all' | 'unpriced';
  setPriceFilter: (v: 'priced' | 'all' | 'unpriced') => void;
  total: number;
  filtered: number;
  /** Buttons shown after the filter chips, e.g. the price list upload. */
  actions?: React.ReactNode;
  /** The colour legend with its state filters (`LegendFilter`). */
  legend?: React.ReactNode;
}

export function FilterToolbar(props: FilterToolbarProps) {
  return (
    <div className="pt-toolbar">
      <DsSearchInput
        className="pt-search"
        value={props.query}
        onSearch={props.setQuery}
        debounce={0}
        placeholder="Art.-Nr., Name, Hersteller…"
        autoFocus
      />

      <div className="pt-filter-field">
        <DsMultiSelect
          label="Hersteller"
          options={facetOptions(props.manufacturers)}
          selected={props.selectedMfgs}
          onChange={props.setSelectedMfgs}
          searchPlaceholder="Hersteller suchen…"
        />
      </div>
      <div className="pt-filter-field">
        <DsMultiSelect
          label="Warengruppe"
          options={facetOptions(props.itemGroups)}
          selected={props.selectedGroups}
          onChange={props.setSelectedGroups}
          searchPlaceholder="Warengruppe suchen…"
        />
      </div>

      <span className="pt-filter-sep" />

      <ToggleChip on={props.onlyDemand} onClick={() => props.setOnlyDemand(!props.onlyDemand)}>
        Nur Bedarf
      </ToggleChip>
      <ToggleChip on={props.onlyAnomalies} onClick={() => props.setOnlyAnomalies(!props.onlyAnomalies)}>
        Auffälligkeiten
      </ToggleChip>
      <ToggleChip on={props.onlyEdited} onClick={() => props.setOnlyEdited(!props.onlyEdited)}>
        Nur Bearbeitete
      </ToggleChip>
      <ToggleChip on={props.onlyNotInSap} onClick={() => props.setOnlyNotInSap(!props.onlyNotInSap)}>
        Nicht in SAP
      </ToggleChip>
      <PriceFilterChip value={props.priceFilter} onChange={props.setPriceFilter} />
      {props.actions}

      {props.legend}

      <span className="pt-hit-count">
        <b>{props.filtered}</b> / {props.total}
      </span>
    </div>
  );
}

function ToggleChip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button className={`ds-toggle-chip${on ? ' on' : ''}`} onClick={onClick}>
      <span style={{
        width: 8, height: 8, borderRadius: 1,
        background: 'var(--accent)', opacity: on ? 1 : 0,
        display: 'inline-block',
      }} />
      {children}
    </button>
  );
}

const PRICE_FILTER_CYCLE: ('priced' | 'all' | 'unpriced')[] = ['priced', 'all', 'unpriced'];
const PRICE_FILTER_LABELS: Record<string, string> = {
  priced: 'Mit Preisen',
  all: 'Alle Artikel',
  unpriced: 'Ohne Preise',
};

function PriceFilterChip({ value, onChange }: { value: 'priced' | 'all' | 'unpriced'; onChange: (v: 'priced' | 'all' | 'unpriced') => void }) {
  const next = () => {
    const idx = PRICE_FILTER_CYCLE.indexOf(value);
    onChange(PRICE_FILTER_CYCLE[(idx + 1) % PRICE_FILTER_CYCLE.length]);
  };

  return (
    <button className={`ds-toggle-chip${value !== 'priced' ? ' on' : ''}`} onClick={next}>
      <span style={{
        width: 8, height: 8, borderRadius: 1,
        background: value === 'unpriced' ? 'var(--neg)' : 'var(--accent)',
        opacity: value !== 'priced' ? 1 : 0,
        display: 'inline-block',
      }} />
      {PRICE_FILTER_LABELS[value]}
    </button>
  );
}

/** Facets (with how many articles each) as options of a filter list. */
function facetOptions(facets: PreisToolFacet[]): DsMultiSelectOption<number>[] {
  return facets.map((f) => ({ value: f.id, label: f.name, count: f.count }));
}
